import { CurrencyPipe, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MatDialogModule, MatProgressBarModule } from '@angular/material';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OWL_DATE_TIME_LOCALE, OwlDateTimeIntl } from 'ng-pick-datetime';
import { OWL_COLOR_PICKER_SCROLL_STRATEGY_PROVIDER_FACTORY } from 'owl-ng/color-picker/color-picker.component';
import { AppConfig } from './app-config';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AdminGuard } from './backoffice/components/common/guards/admin.guard';
import { AnagraficaGuard } from './backoffice/components/common/guards/anagrafica.guard';
import { AnagraficaSemirimorchioGuard } from './backoffice/components/common/guards/anagrafica.semirimorchio.guard';
import { AnagraficaSicurezzaGuard } from './backoffice/components/common/guards/anagrafica.sicurezza.guard';
import { AuthGuard } from './backoffice/components/common/guards/auth.guard';
import { ControlloreAccessiMobileGuard } from './backoffice/components/common/guards/controlloreAccessiMobile.guard';
import { ControlloreBanchinaGuard } from './backoffice/components/common/guards/controlloreBanchina.guard';
import {
  ControlloreCoordinatoreSecurityGuard
} from './backoffice/components/common/guards/controlloreCoordinatoreSecurity.guard';
import { ControlloreReportCapoturnoGuard } from './backoffice/components/common/guards/controlloreReportCapoturno.guard';
import {
  ControlloreResponsabilePostazioneGuard
} from './backoffice/components/common/guards/controlloreResponsabilePostazione.guard';
import { ControlloreRxArcoSnifferGuard } from './backoffice/components/common/guards/controlloreRxArcoSniffer.guard';
import { ControlloreSecurityPaxCrocieraGuard } from './backoffice/components/common/guards/controlloreSecurityPaxCrociera.guard';
import {
  ControlloreTransitoCrociereGuard
} from './backoffice/components/common/guards/controlloreTransitoCrociere.guard';
import { ControlloServizioQualita } from './backoffice/components/common/guards/controlloServizioQualita.guard';
import { DocumentiDipendentiViewGuard } from './backoffice/components/common/guards/documentiDipendentiView.guard';
import { DocumentiVariViewGuard } from './backoffice/components/common/guards/documentiVariView.guard';
import { IngressoUscitaSemirimorchioGuard } from './backoffice/components/common/guards/ingressoUscitaSemirimorchio.guard';
import { LoginGuard } from './backoffice/components/common/guards/login.guard';
import { NegozioModificaGuard } from './backoffice/components/common/guards/negozioModifica.guard';
import { NegozioViewGuard } from './backoffice/components/common/guards/negozioView.guard';
import { OrdineServizioModificaGuard } from './backoffice/components/common/guards/ordineServizioModifica.guard';
import { OrdineServizioViewGuard } from './backoffice/components/common/guards/ordineServizioView.guard';
import { ReportControlloBanchinaGuard } from './backoffice/components/common/guards/reportControlloBanchina.guard';
import { ReportCoordinatoreSecurityGuard } from './backoffice/components/common/guards/reportCoordinatoreSecurity.guard';
import {
  ReportResponsabilePostazioneGuard
} from './backoffice/components/common/guards/reportResponsabilePostazione.guard';
import { ReportRxArcoSnifferGuard } from './backoffice/components/common/guards/reportRxArcoSniffer.guard';
import { ReportSecurityPaxCrocieraGuard } from './backoffice/components/common/guards/reportSecurityPaxCrociera.guard';
import { ReportSecurityTraghettiGuard } from './backoffice/components/common/guards/reportSecurityTraghetti.guard';
import { ReportSicurezzaGuard } from './backoffice/components/common/guards/reportSicurezza.guard';
import { ReportTransitoCrociereGuard } from './backoffice/components/common/guards/reportTransitoCrociere.guard';
import { SemirimorchioGuard } from './backoffice/components/common/guards/semirimorchio.guard';
import { SicurezzaModificaGuard } from './backoffice/components/common/guards/sicurezzaModifica.guard';
import { SicurezzaViewGuard } from './backoffice/components/common/guards/sicurezzaView.guard';
import { SostaModificaGuard } from './backoffice/components/common/guards/sostaModifica.guard';
import { SostaViewGuard } from './backoffice/components/common/guards/sostaView.guard';
import { MatSnackBarComponent } from './backoffice/components/mat-snack-bar/mat-snack-bar.component';
import { HttpInterceptorService } from './interceptor/httpinterceptor.service';
import { HttpUtilService } from './services/http/http.util.service';

export function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

export class MyIntlLabels extends OwlDateTimeIntl {
  public cancelBtnLabel = 'Annulla';
  public setBtnLabel = 'Conferma';
  public rangeFromLabel = 'Dal Giorno';
  public rangeToLabel = 'Al Giorno';
}

@NgModule({
  declarations: [
    AppComponent,
    MatSnackBarComponent,
  ],
  exports: [],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FontAwesomeModule,
    BrowserAnimationsModule,
    MatSnackBarModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressBarModule
  ],
  bootstrap: [AppComponent],
  providers: [
    AppConfig,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppConfig], multi: true
    },
    { provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true
    },
    {provide: OWL_DATE_TIME_LOCALE, useValue: 'it'},
    {provide: OWL_COLOR_PICKER_SCROLL_STRATEGY_PROVIDER_FACTORY, useValue: 'it'},
    {provide: OwlDateTimeIntl, useClass: MyIntlLabels},
    DatePipe,
    HttpUtilService,
    AuthGuard,
    CurrencyPipe,
    LoginGuard,
    AdminGuard,
    AnagraficaGuard,
    AnagraficaSicurezzaGuard,
    NegozioViewGuard,
    NegozioModificaGuard,
    OrdineServizioViewGuard,
    OrdineServizioModificaGuard,
    ReportSicurezzaGuard,
    SicurezzaViewGuard,
    SicurezzaModificaGuard,
    SostaViewGuard,
    SostaModificaGuard,
    ControlloServizioQualita,
    AnagraficaSemirimorchioGuard,
    SemirimorchioGuard,
    IngressoUscitaSemirimorchioGuard,
    ControlloreCoordinatoreSecurityGuard,
    ControlloreReportCapoturnoGuard,
    ControlloreResponsabilePostazioneGuard,
    ControlloreAccessiMobileGuard,
    ReportSecurityTraghettiGuard,
    ControlloreTransitoCrociereGuard,
    ControlloreBanchinaGuard,
    ReportControlloBanchinaGuard,
    ReportResponsabilePostazioneGuard,
    ReportTransitoCrociereGuard,
    ReportCoordinatoreSecurityGuard,
    ReportRxArcoSnifferGuard,
    ControlloreRxArcoSnifferGuard,
    ControlloreSecurityPaxCrocieraGuard,
    ReportSecurityPaxCrocieraGuard,
    DocumentiDipendentiViewGuard,
    DocumentiVariViewGuard
  ],
  entryComponents: [
    MatSnackBarComponent,
  ]
})
export class AppModule {
}
